import { and, eq, isNotNull, sql } from 'drizzle-orm'

import type { InsertNotification, NotificationCategory } from '@package/drizzle/schema'
import { Notification, NotificationEvent } from '@package/drizzle/schema'

import type { DatabaseOrTransaction, NotificationContext, NotificationSnapshot } from './types'
import { canNotify } from './policy'

export interface EmitRollupDeltaInput {
  type: string
  category: NotificationCategory
  /** Null for cron-generated roll-ups. */
  actorId: string | null
  recipientIds: string[]
  /**
   * The bucket. Every call sharing this key folds into one card instead of adding a row.
   * Unlike `emit`'s optional `dedupeKey` — where a collision means "already sent, do
   * nothing" — here a collision is the normal path and means "add to the running total".
   */
  dedupeKey: string
  /**
   * Plain string, not `ShareEntityType`: `notification_event.entityType` is a text column
   * so a notification kind can point at something the share enum never covers (a `COURSE`).
   */
  entity?: { type: string; id: string }
  /** Written on first occurrence only. A roll-up card must not rewrite its own history. */
  snapshot?: NotificationSnapshot
  context?: NotificationContext
  /** How much this call adds to the running total. Default 1; 0 only adds recipients. */
  subjectDelta?: number
  /**
   * Re-open the card when it changes: clear `readAt` and move it back to the head of the
   * feed. Default true — the card now says something other than what was read.
   */
  resurface?: boolean
}

export interface EmitRollupDeltaResult {
  eventId: string
  /** Running total after this call. */
  subjectCount: number
  /** False when this call folded into an existing card. */
  created: boolean
}

/**
 * Adds one occurrence to a grouped notification, creating the card on first occurrence.
 *
 * The counting sibling of {@link emitRollup}. The two group the same way and differ in what
 * the count means. `emitRollup` counts DISTINCT subjects — nineteen children asking for
 * premium is nineteen, and the same child asking twice is still nineteen — so it carries the
 * set it has already counted. This one counts OCCURRENCES: a teacher grading four pieces of
 * one child's work is four, and `subjectDelta: 0` adds recipients to an open card without
 * moving its total at all. A cron digest needs the second; a per-person request needs the
 * first.
 *
 * This is the answer to burst notifications. Thirty students submitting work inside a
 * minute must not be thirty cards and a badge reading "30" — they are one card whose
 * count climbs. `emit` cannot express that: its `dedupeKey` collision path is a no-op,
 * which would silently drop students two through thirty.
 *
 * The bucket is entirely the caller's: `dedupeKey` decides what counts as "the same
 * notification", and a Jakarta day key (`getJakartaDayKey`) is the usual last segment so
 * one card never spans two days. See `docs/data-model.md`.
 *
 * Synchronous and transactional, exactly like `emit` — the feed must not wait on a queue.
 */
export async function emitRollupDelta(
  db: DatabaseOrTransaction,
  input: EmitRollupDeltaInput,
): Promise<EmitRollupDeltaResult> {
  const recipientIds = [...new Set(input.recipientIds)]
  const subjectDelta = input.subjectDelta ?? 1

  if (recipientIds.length === 0) {
    throw new Error(`emitRollupDelta called with no recipients for ${input.type}`)
  }

  if (input.actorId) {
    const allowed = await canNotify(db, { actorId: input.actorId, recipientIds, type: input.type })
    if (!allowed) {
      throw new Error(`canNotify rejected ${input.type} from ${input.actorId}`)
    }
  }

  return db.transaction(async (tx) => {
    const now = new Date()

    // Insert-then-update rather than a single `onConflictDoUpdate`: the two paths have to
    // be told apart exactly. `returning()` after `onConflictDoNothing` yields a row only
    // when this call really created the card, which no arithmetic on the merged row can
    // establish (a first occurrence and a second one are indistinguishable once merged).
    const [inserted] = await tx
      .insert(NotificationEvent)
      .values({
        type: input.type,
        actorId: input.actorId,
        entityType: input.entity?.type ?? null,
        entityId: input.entity?.id ?? null,
        snapshot: input.snapshot ?? {},
        context: input.context ?? {},
        dedupeKey: input.dedupeKey,
        recipientCount: recipientIds.length,
        subjectCount: subjectDelta,
        lastActivityAt: now,
      })
      .onConflictDoNothing({ target: NotificationEvent.dedupeKey })
      .returning()

    const existing =
      inserted ??
      (await tx.query.NotificationEvent.findFirst({ where: eq(NotificationEvent.dedupeKey, input.dedupeKey) }))

    // Only reachable if the row vanished between the failed insert and this read.
    if (!existing) throw new Error(`emitRollupDelta lost the event for ${input.dedupeKey}`)

    const created = Boolean(inserted)

    const rows: InsertNotification[] = recipientIds.map((userId) => ({
      eventId: existing.id,
      userId,
      audience: 'RECIPIENT',
      category: input.category,
    }))

    // Rows that were really inserted — a roll-up can gain recipients over its life (a cron
    // re-run after more children were enrolled), and only those must move `recipientCount`.
    // Bare `returning()`, not a projection: `Database` is a union of the neon-http and
    // node-postgres drivers and only the no-argument overload survives that union.
    const insertedRows = await tx.insert(Notification).values(rows).onConflictDoNothing().returning()

    if (created) {
      return { eventId: existing.id, subjectCount: existing.subjectCount, created }
    }

    const [updated] = await tx
      .update(NotificationEvent)
      .set({
        subjectCount: sql`${NotificationEvent.subjectCount} + ${subjectDelta}`,
        recipientCount: sql`${NotificationEvent.recipientCount} + ${insertedRows.length}`,
        lastActivityAt: now,
        // `snapshot`, `context`, `actorId` and the entity ref are deliberately untouched:
        // they were frozen by the first occurrence, and rewriting them would make a card
        // the user has already seen change its subject.
      })
      .where(eq(NotificationEvent.id, existing.id))
      .returning()

    if (input.resurface ?? true) {
      // Only rows that were actually read are touched, so an untouched unread card keeps
      // its position instead of jumping every time the count ticks.
      //
      // `createdAt` moves with `readAt` because the feed is ordered by it: a card that
      // silently regained its unread dot three screens down is a card nobody reads.
      await tx
        .update(Notification)
        .set({ readAt: null, createdAt: now })
        .where(
          and(
            eq(Notification.eventId, existing.id),
            eq(Notification.audience, 'RECIPIENT'),
            isNotNull(Notification.readAt),
          ),
        )
    }

    return {
      eventId: existing.id,
      subjectCount: updated?.subjectCount ?? existing.subjectCount + subjectDelta,
      created,
    }
  })
}
